import { motion } from "framer-motion";

// Line drawing of a Kerala sloped-roof house, drafted stroke by stroke.
const STROKES = [
  // ground + plinth
  "M20 322 H620",
  "M80 322 V304 H560 V322",
  // walls
  "M110 304 V196 H530 V304",
  // main gable roof with deep eaves
  "M56 206 L320 56 L584 206",
  "M98 192 L320 68 L542 192",
  // gable vent (Kerala style)
  "M320 104 L292 136 H348 Z",
  // verandah beam + columns
  "M126 226 H262",
  "M150 304 V226",
  "M238 304 V226",
  // door
  "M300 304 V236 H342 V304",
  // window with mullion
  "M400 284 V234 H472 V284 Z",
  "M436 234 V284",
  // steps
  "M300 322 H342 M292 331 H350",
];

const DIMS = [
  { d: "M110 346 H530", ticks: ["M110 339 V353", "M530 339 V353"], label: "12.40 M", x: 320, y: 362 },
  { d: "M606 56 V304", ticks: ["M599 56 H613", "M599 304 H613"], label: "7.6 M", x: 618, y: 184, rotate: 90 },
];

export const HouseDrawing = ({ className = "", stroke = "currentColor", delay = 0, duration = 2.4, loop = false }) => {
  const total = STROKES.length;
  return (
    <svg viewBox="0 0 640 380" fill="none" className={className} aria-hidden="true">
      {STROKES.map((d, i) => (
        <motion.path
          key={d}
          d={d}
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { duration: duration / total * 1.6, delay: delay + (i * duration) / total, ease: "easeInOut", ...(loop ? { repeat: Infinity, repeatType: "reverse", repeatDelay: 1.5 } : {}) },
            opacity: { duration: 0.2, delay: delay + (i * duration) / total },
          }}
        />
      ))}
      {DIMS.map((dim, i) => (
        <motion.g key={dim.d} stroke={stroke} strokeWidth="1" initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: delay + duration + 0.2 + i * 0.2, duration: 0.6 }}>
          <motion.path d={dim.d} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: delay + duration + 0.2 + i * 0.2, duration: 0.6, ease: "easeInOut" }} />
          {dim.ticks.map((t) => <path key={t} d={t} />)}
          <text x={dim.x} y={dim.y} fill={stroke} stroke="none" fontSize="11" fontFamily="IBM Plex Mono, monospace" textAnchor="middle" transform={dim.rotate ? `rotate(${dim.rotate} ${dim.x} ${dim.y})` : undefined}>{dim.label}</text>
        </motion.g>
      ))}
    </svg>
  );
};

// Faint drafting-paper grid used as a section backdrop.
export const BlueprintGrid = ({ className = "", dark = false }) => (
  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${dark ? "bp-grid-dark" : "bp-grid"} ${className}`} />
);

// CAD-style corner crop marks that draw in around an image.
export const CropMarks = ({ delay = 0.6, color = "#161614" }) => (
  <>
    {[["top-0 left-0", "M1 10 V1 H10"], ["top-0 right-0", "M14 1 H23 V10"], ["bottom-0 left-0", "M1 14 V23 H10"], ["bottom-0 right-0", "M23 14 V23 H14"]].map(([pos, d], i) => (
      <motion.svg key={pos} viewBox="0 0 24 24" className={`pointer-events-none absolute ${pos} w-6 h-6`} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: delay + i * 0.08, duration: 0.5 }} aria-hidden="true">
        <path d={d} stroke={color} strokeWidth="1.5" fill="none" />
      </motion.svg>
    ))}
  </>
);
